import { yupResolver } from '@hookform/resolvers/yup'
import * as React from 'react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { generatePath, useHistory } from 'react-router'
import { BulletList, Wrap } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { DropZoneMultipleArea } from 'shared/components/DropZoneArea/DropZoneMultipleArea'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { PhoneInput } from 'shared/components/MaskedInput/PhoneInput'
import { Back } from 'shared/components/Nav/Back'
import {
  EmploymentHistoryContainer,
  EmploymentHistoryDotLookup,
  EmploymentHistoryRowContent,
  EmploymentHistoryRowDoubleField,
} from 'shared/components/Onboarding/EmploymentHistory/EmploymentHistory.styles'
import { RadioButtonGroup } from 'shared/components/RadioButtonGroup/RadioButtonGroup'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { convertMobileForAPI } from 'shared/helpers/formatters'
import { useEmploymentHistory } from 'shared/hooks/query/useEmploymentHistory'
import { useScrollToErrors } from 'shared/hooks/useScrollToErrors'
import { midGray, primaryBlack } from 'shared/theme/colors'

import { ErrorMessage, ErrorMessageList } from '../../../../components/Message/ErrorMessage'
import { YesNoRadioOptions } from '../../../../helpers/utils'
import { getEmploymentHistoryItemCache } from '../../../../hooks/query/useEmploymentHistoryItem'
import { YesNo } from '../../../../models'
import { DotAndMcNumber, getDotAndMcNumber } from '../../../../models/dotNumber'
import {
  createEmploymentHistoryDocuments,
  EmploymentHistoryPayload,
  EmploymentJob,
  updateEmploymentHistoryItem,
} from '../../../../models/employment'
import { User } from '../../../../models/user'
import { useUserProvider } from '../../../Context/UserContext'
import { EMPLOYMENT_ROUTES } from '../EmploymentHistory.constants'
import {
  EmploymentHistoryFmcsrForm,
  getEmploymentHistoryFmcsrFormSchema,
  getEmploymentHistoryFmcsrInitialErrors,
  getEmploymentHistoryFmcsrInitialForm,
} from './EmploymentHistoryFmcsrValidationForm.constants'

type Props = {
  isEditing: boolean
  employmentHistoryItem: EmploymentJob
  user: User
}

const parseFormValues = (
  values: EmploymentHistoryFmcsrForm,
  dotNumber: string,
  canContact: YesNo
): EmploymentHistoryPayload => ({
  details: {
    dot_number: dotNumber.trim(),
    can_contact_employer: canContact === YesNo.YES,
    contact_name: values.contactName?.trim(),
    contact_email: values.email?.trim(),
    contact_phone: convertMobileForAPI(values.phoneNumber?.trim()),
    subject_to_fmcsr_regulations: values.fmcsrValid === YesNo.YES,
    subject_to_drug_and_alcohol_testing: values.drugAlcohol === YesNo.YES,
    is_own_authority: values.isOwnAuthority === YesNo.YES,
  },
})

export function EmploymentHistoryFmcsrValidationForm({ user, isEditing, employmentHistoryItem }: Props) {
  const history = useHistory()
  const [dotResponse, setDotResponse] = useState<DotAndMcNumber | null>(null)
  const [showDotConfirmationModal, setShowDotConfirmationModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { refetch: refetchEmploymentHistory } = useEmploymentHistory({ userId: user.id })
  const queryClient = useQueryClient()
  const { showBanner } = useUserProvider()

  const {
    control,
    handleSubmit,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm<EmploymentHistoryFmcsrForm>({
    mode: 'onSubmit',
    defaultValues: getEmploymentHistoryFmcsrInitialForm(employmentHistoryItem),
    resolver: yupResolver(getEmploymentHistoryFmcsrFormSchema(employmentHistoryItem)),
  })

  const isOwnAuthority = watch('isOwnAuthority') === YesNo.YES
  const mayContact = watch('mayContact')
  const setDotInvalid = () => setError('dotNumber', { message: 'Enter a valid DOT number' })
  const disableMcDotValidationFlag = user.featureFlags?.disableMcDotValidation
  useScrollToErrors(errors)

  const onSubmit = async (formData: EmploymentHistoryFmcsrForm) => {
    setIsSubmitting(true)

    if (disableMcDotValidationFlag) {
      return onConfirm(formData.dotNumber)
    }

    try {
      const _dotResponse = await getDotAndMcNumber({ dotNumber: formData.dotNumber })
      if (!_dotResponse) return setDotInvalid()
      setDotResponse(_dotResponse)
      setShowDotConfirmationModal(true)
    } catch (err) {
      setDotInvalid()
    }
    setIsSubmitting(false)
  }

  async function onConfirm(dotNumber: string | number) {
    setIsSubmitting(true)
    const values = getValues()

    try {
      if (values.driverDocuments.length) {
        const formData = new FormData()
        values.driverDocuments.forEach((document) => {
          if (document instanceof File) {
            formData.append('documents', document)
          }
        })

        await createEmploymentHistoryDocuments(user.id, employmentHistoryItem.id, formData)
      }

      if (dotNumber) {
        await updateEmploymentHistoryItem(
          user.id,
          employmentHistoryItem.id,
          parseFormValues(
            values,
            `${dotNumber}`.replace(/\D/g, ''),
            values.mayContact ||
              (!employmentHistoryItem.details.isCurrentlyWorkingHere ? YesNo.YES : YesNo.NO)
          )
        )
        handleCloseDotConfirmationModal()
        await refetchEmploymentHistory()
        queryClient.removeQueries({
          queryKey: getEmploymentHistoryItemCache(employmentHistoryItem?.id!, user.id),
          exact: true,
        })
        history.push(EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY)
      }
    } catch (err: any) {
      showBanner('error', err)
    }
    setIsSubmitting(false)
  }

  const handleCloseDotConfirmationModal = () => {
    setShowDotConfirmationModal(false)
  }

  function maybeRenderInitialErrors() {
    const initialErrors = getEmploymentHistoryFmcsrInitialErrors(employmentHistoryItem)

    if (initialErrors.length === 0 || !isEditing) return null

    return (
      <>
        <ErrorMessage>
          <Text variant="header4">Please complete the missing information:</Text>
          <ErrorMessageList>
            {initialErrors.map((error) => (
              <li key={error}>
                <Text variant="paragraph2">{error}</Text>
              </li>
            ))}
          </ErrorMessageList>
        </ErrorMessage>
        <Box m={3} />
      </>
    )
  }

  function renderOptionalString() {
    if (isOwnAuthority) return '(optional)'
    if (mayContact === YesNo.NO && employmentHistoryItem.details.isCurrentlyWorkingHere) return ''
    return '(optional)'
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Back
          href={generatePath(EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY_EDIT, {
            id: employmentHistoryItem.id,
          })}
        />
        <EmploymentHistoryContainer>
          <Text variant="header2" align="center">
            {isEditing ? 'Edit' : 'Add a'} your position details
          </Text>
          <Text variant="paragraph1" align="center" color={midGray} mt={1}>
            {employmentHistoryItem.details.companyName}
          </Text>

          <Box m={2} />
          <EmploymentHistoryRowContent $vertical>{maybeRenderInitialErrors()}</EmploymentHistoryRowContent>

          <EmploymentHistoryRowContent $vertical>
            <Controller
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Employer's DOT Number"
                  error={fieldState.error?.message}
                  noMargin
                />
              )}
              name="dotNumber"
              control={control}
            />
            <Box mt={errors.dotNumber ? 2 : 0} />
            <EmploymentHistoryDotLookup>
              <ExternalLink variant="paragraph2" href={'https://safer.fmcsa.dot.gov/CompanySnapshot.aspx'}>
                Not sure? Look it up
              </ExternalLink>
            </EmploymentHistoryDotLookup>
          </EmploymentHistoryRowContent>

          <Box m={2} />
          <EmploymentHistoryRowContent>
            <Controller
              render={({ field, fieldState }) => (
                <RadioButtonGroup
                  {...field}
                  label="Did you operate under your own authority?"
                  error={fieldState.error?.message}
                  options={YesNoRadioOptions}
                  width="100%"
                  row
                  noMargin
                />
              )}
              name="isOwnAuthority"
              control={control}
            />
          </EmploymentHistoryRowContent>

          <Box m={1} />
          <EmploymentHistoryRowContent>
            <Controller
              render={({ field, fieldState }) => (
                <RadioButtonGroup
                  {...field}
                  label="Was this job subject to Federal Motor Carrier Safety Regulations?"
                  error={fieldState.error?.message}
                  options={YesNoRadioOptions}
                  width="100%"
                  row
                  noMargin
                />
              )}
              name="fmcsrValid"
              control={control}
            />
          </EmploymentHistoryRowContent>

          <Box m={1} />
          <EmploymentHistoryRowContent>
            <Controller
              render={({ field, fieldState }) => (
                <RadioButtonGroup
                  {...field}
                  label="Was this job subject to drug and alcohol testing?"
                  error={fieldState.error?.message}
                  options={YesNoRadioOptions}
                  width="100%"
                  row
                  noMargin
                />
              )}
              name="drugAlcohol"
              control={control}
            />
          </EmploymentHistoryRowContent>

          {employmentHistoryItem.details.isCurrentlyWorkingHere && !isOwnAuthority && (
            <>
              <Box m={1} />
              <EmploymentHistoryRowContent>
                <Controller
                  render={({ field, fieldState }) => (
                    <RadioButtonGroup
                      {...field}
                      label="May we contact this employer?"
                      error={fieldState.error?.message}
                      options={YesNoRadioOptions}
                      width="100%"
                      row
                      noMargin
                    />
                  )}
                  name="mayContact"
                  control={control}
                />
              </EmploymentHistoryRowContent>
            </>
          )}
          {(!employmentHistoryItem.details.isCurrentlyWorkingHere || mayContact === YesNo.YES) &&
            !isOwnAuthority && (
              <>
                <Box m={employmentHistoryItem.details.isCurrentlyWorkingHere && !isOwnAuthority ? 0.5 : 1} />
                <EmploymentHistoryRowContent>
                  <Text variant="header4" mb={1}>
                    Add a contact who can verify your driving experience
                  </Text>
                </EmploymentHistoryRowContent>
                <EmploymentHistoryRowContent>
                  <Controller
                    render={({ field, fieldState }) => (
                      <PhoneInput
                        {...field}
                        label="Phone number"
                        error={Boolean(fieldState.error?.message)}
                        noMargin
                      />
                    )}
                    name="phoneNumber"
                    control={control}
                  />
                </EmploymentHistoryRowContent>

                <Box mt={1} />
                <EmploymentHistoryRowDoubleField $noMargin>
                  <Controller
                    render={({ field, fieldState }) => (
                      <TextInput {...field} label="Email (optional)" error={fieldState.error?.message} />
                    )}
                    name="email"
                    control={control}
                  />
                  <Controller
                    render={({ field, fieldState }) => (
                      <TextInput
                        {...field}
                        label="Name (optional)"
                        error={Boolean(fieldState.error)}
                        noMargin
                      />
                    )}
                    name="contactName"
                    control={control}
                  />
                </EmploymentHistoryRowDoubleField>
                <EmploymentHistoryRowContent>
                  <Text variant="paragraph3" color={midGray} fontWeight={600} align="left">
                    Email and name will speed up verification
                  </Text>
                </EmploymentHistoryRowContent>
              </>
            )}

          <Box m={1} />
          <EmploymentHistoryRowContent>
            <Text variant="header4" mb={1}>
              Upload any of the following for faster verification {renderOptionalString()}
            </Text>
          </EmploymentHistoryRowContent>
          <EmploymentHistoryRowContent>
            <BulletList>
              <li>
                <Text variant="paragraph2">ELD logs while driving under this authority</Text>
              </li>
              <li>
                <Text variant="paragraph2">
                  Passed random drug test or drug consortium letter while under this authority
                </Text>
              </li>
              <li>
                <Text variant="paragraph2">1099 from this authority</Text>
              </li>
              <li>
                <Text variant="paragraph2">
                  Bill of lading or rate con for loads you ran under this authority
                </Text>
              </li>
            </BulletList>
          </EmploymentHistoryRowContent>
          <Box m={1} />

          <Controller
            render={({ field, fieldState }) => (
              <DropZoneMultipleArea
                {...field}
                hasError={Boolean(fieldState.error)}
                uploadedFiles={employmentHistoryItem?.driverDocuments?.map((d) => d.url) ?? []}
              />
            )}
            name="driverDocuments"
            control={control}
          />

          <Box m={4} />

          <Button type="submit" variant="primary" size="extraLarge" isLoading={isSubmitting}>
            Save
          </Button>
        </EmploymentHistoryContainer>
      </form>

      {dotResponse && (
        <SimpleDialog
          isOpen={showDotConfirmationModal}
          onClose={handleCloseDotConfirmationModal}
          maxWidth="472px"
        >
          <Wrap width="700px" maxWidth="100%">
            <Text variant="header2" fontColor={primaryBlack} mb={2} align="left">
              Confirm your employer
            </Text>
            <Text variant="paragraph1" align="left">
              {dotResponse.dotNumber?.legalName}
            </Text>
            <Text variant="paragraph1" align="left">
              {dotResponse.dotNumber?.phyCity}, {dotResponse.dotNumber?.phyState}
            </Text>
            <Box m={5} />
            <FlexBox isColumn justifyContent="center" alignItems="center" width="100%">
              <Button
                variant="primary"
                onClick={() => onConfirm(dotResponse.dotNumber?.dotNumber!)}
                margin="0 auto"
                isLoading={isSubmitting}
              >
                Confirm
              </Button>
              <Box mt={3} />
              <Button variant="text" onClick={handleCloseDotConfirmationModal} margin="0 auto">
                Edit DOT number
              </Button>
            </FlexBox>
          </Wrap>
        </SimpleDialog>
      )}
    </>
  )
}
